import React, { useEffect, useMemo, useState } from 'react';
import { Box, Inscription, TextWithPointer } from '@common-fe/common-fe';
import styled from 'styled-components';

import Tip from '@/components/elements/Tip/Tip';

import CategorizedHint, { CategorizedItem } from './CategorizedHint';

const StyledList = styled(Box)`
  span:not(:last-child) {
    margin-right: ${({ theme }) => theme.spacings.spacing4};
    &:after {
      content: ',';
    }
  }
`;

const countOverflowingTexts = (texts?: string[], symbolCount?: number) => {
  if (!texts) {
    return [];
  }
  const maxSymbolCount = symbolCount || 38;
  const filterArrayByCharacterLimit = (arr: string[], limit: number): string[] => {
    let totalLength = 0;
    let index = 0;
  
    while (index < arr.length && totalLength + arr[index].length <= limit) {
      totalLength += arr[index].length;
      index++;
    }

    if (index < arr.length && totalLength + arr[index].length > limit) {
      index++;
    }
  
    return arr.slice(0, index);
  };

  return filterArrayByCharacterLimit(texts,maxSymbolCount);
};

interface ClickableItem {
  title?: string;
  onClick: () => void;
}

interface Props {
  categorizedItems?: CategorizedItem[];
  items: string[];
  clickableItems?: ClickableItem[];
  isAllMode?: boolean;
  allModeTitle?: string;
  showMoreLength?: number;
  color?: string;
  itemsTextStyles?: React.CSSProperties;
  showMoreSymbolsLength?: number;
}

const ManyItemsTooltip: React.FC<Props> = ({
  categorizedItems,
  items,
  clickableItems,
  isAllMode,
  allModeTitle = 'All types',
  showMoreLength,
  color,
  itemsTextStyles,
  showMoreSymbolsLength,
}) => {
  const [availableText, setAvailableText] = useState<string[]>([]);
  const currentShowMoreLength = useMemo(() => showMoreLength || availableText.length, [availableText, showMoreLength]);
  const hint = useMemo(() => categorizedItems?.length
    ? <CategorizedHint categorizedItems={categorizedItems?.slice(currentShowMoreLength) || []} />
    : null, [categorizedItems, currentShowMoreLength]);

  useEffect(() => {
    const showMoreCount = countOverflowingTexts(items, showMoreSymbolsLength);
    setAvailableText(showMoreCount);
  }, [items, showMoreSymbolsLength]);

  if (isAllMode) {
    return (
      <Box direction="row" align="center">
        <Inscription
          color="textTitle"
          {...itemsTextStyles ? { style: itemsTextStyles } : {}}
        >
          {`${allModeTitle} (${categorizedItems?.length || items.length})`}
        </Inscription>
        <Tip hasIcon items={items} hint={hint} />
      </Box>
    );
  }

  if (currentShowMoreLength && items.length > currentShowMoreLength) {
    return (
      <Box direction="row" align="center">
        {clickableItems?.length ? (
          <StyledList direction="row">{clickableItems.slice(0, currentShowMoreLength).map((clickableItem) => (
            <TextWithPointer
              key={clickableItem.title}
              onClick={clickableItem.onClick}
              color={color || 'textSecondary'}
              title={clickableItem.title}
              size="small"
              ellipsisMode
              weight={500}
              style={{ cursor: 'pointer', lineHeight: '18px', ...itemsTextStyles }}
            >
              {clickableItem.title}
            </TextWithPointer>
          ))}
          </StyledList>
        ) : (
          <TextWithPointer
            color={color || 'textSecondary'}
            title={items.slice(0, currentShowMoreLength).join(', ')}
            size="small"
            ellipsisMode
            weight={500}
            style={{ lineHeight: '18px', ...itemsTextStyles }}
          >
            {items.slice(0, currentShowMoreLength).join(', ')},
          </TextWithPointer>
        )}
        <Box flex="grow">
          <Tip
            items={items.slice(currentShowMoreLength)}
            hint={hint}
          >
            <Inscription
              color="info"
              cursor="pointer"
            >
              &nbsp;+{(categorizedItems?.length || items?.length) - currentShowMoreLength}
            </Inscription>
          </Tip>
        </Box>
      </Box>
    );
  }

  if (clickableItems?.length) {
    return (
      <StyledList direction="row">{clickableItems.map((clickableItem) => (
        <TextWithPointer
          key={clickableItem.title}
          onClick={clickableItem.onClick}
          color={color || 'textSecondary'}
          title={clickableItem.title}
          size="small"
          ellipsisMode
          weight={500}
          style={{ cursor: 'pointer', lineHeight: '18px', ...itemsTextStyles }}
        >
          {clickableItem.title}
        </TextWithPointer>
      ))}
      </StyledList>
    );
  }

  return (
    <TextWithPointer
      color={color || 'textSecondary'}
      title={items.join(', ')}
      size="small"
      ellipsisMode
      weight={500}
      style={{ lineHeight: '18px', ...itemsTextStyles }}
    >
      {items.join(', ')}
    </TextWithPointer>
  );
};

export default ManyItemsTooltip;
